import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Upload, X } from "lucide-react";
import type { ConstantSumCluster } from "./pollTypes";

interface PollConstantSumEditorProps {
  clusters: ConstantSumCluster[];
  hideZeroAllocated: boolean;
  setHideZeroAllocated: (value: boolean) => void;
  addCluster: () => void;
  removeCluster: (index: number) => void;
  updateCluster: (index: number, field: keyof ConstantSumCluster, value: string) => void;
  addSubCategory: (clusterIndex: number) => void;
  removeSubCategory: (clusterIndex: number, subIndex: number) => void;
  updateSubCategory: (clusterIndex: number, subIndex: number, field: "label" | "description", value: string) => void;
  handleImageUpload: (clusterIndex: number, file: File) => void;
  handleRemoveImage: (clusterIndex: number, imageUrl: string) => void;
}

export const PollConstantSumEditor = ({
  clusters,
  hideZeroAllocated,
  setHideZeroAllocated,
  addCluster,
  removeCluster,
  updateCluster,
  addSubCategory,
  removeSubCategory,
  updateSubCategory,
  handleImageUpload,
  handleRemoveImage,
}: PollConstantSumEditorProps) => {
  return (
    <>
      <div className="flex items-center justify-between">
        <Label htmlFor="hideZeroAllocated">Hide sub-categories for zero-allocated clusters</Label>
        <Switch id="hideZeroAllocated" checked={hideZeroAllocated} onCheckedChange={setHideZeroAllocated} />
      </div>

      <div className="space-y-4">
        {clusters.map((cluster, clusterIndex) => (
          <Card key={clusterIndex}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Cluster {clusterIndex + 1}</CardTitle>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeCluster(clusterIndex)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Label *</Label>
                <Input
                  value={cluster.label}
                  onChange={(e) => updateCluster(clusterIndex, "label", e.target.value)}
                  placeholder="e.g., Technology"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={cluster.description}
                  onChange={(e) => updateCluster(clusterIndex, "description", e.target.value)}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>
              <div>
                <Label>Image</Label>
                {cluster.image_url ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 border rounded-lg">
                      <img src={cluster.image_url} alt={cluster.label} className="w-16 h-16 object-cover rounded" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveImage(clusterIndex, cluster.image_url)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(clusterIndex, file);
                      }}
                      className="hidden"
                      id={`image-upload-${clusterIndex}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById(`image-upload-${clusterIndex}`)?.click()}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Sub-categories (0-5)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addSubCategory(clusterIndex)}
                    disabled={cluster.subCategories.length >= 5}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Sub
                  </Button>
                </div>

                {cluster.subCategories.map((sub, subIndex) => (
                  <Card key={subIndex} className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Sub {subIndex + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSubCategory(clusterIndex, subIndex)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <Input
                        value={sub.label}
                        onChange={(e) => updateSubCategory(clusterIndex, subIndex, "label", e.target.value)}
                        placeholder="Sub-category label"
                      />
                      <Textarea
                        value={sub.description}
                        onChange={(e) => updateSubCategory(clusterIndex, subIndex, "description", e.target.value)}
                        placeholder="Optional description"
                        rows={1}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" onClick={addCluster} disabled={clusters.length >= 10} className="w-full">
        <Plus className="w-4 h-4 mr-2" />
        Add Cluster ({clusters.length}/10)
      </Button>
    </>
  );
};
